export const SECURE_CASE_OPEN_RETENTION_DAYS = 90;
export const SECURE_CASE_CLOSED_RETENTION_DAYS = 30;
export const SECURE_CASE_DEFAULT_ACCESS_DAYS = 7;
export const SECURE_CASE_MAX_ACCESS_DAYS = 30;
export const SECURE_CASE_MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;
export const SECURE_CASE_MAX_NOTE_LENGTH = 50_000;

export const SECURE_CASE_ALLOWED_FILE_EXTENSIONS: Readonly<Record<string, string>> = {
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  heic: 'image/heic',
  heif: 'image/heif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  json: 'application/json',
  log: 'text/plain',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  mp4: 'video/mp4',
  pdf: 'application/pdf',
  png: 'image/png',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  txt: 'text/plain',
  wav: 'audio/wav',
  webm: 'video/webm',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export type SecureCaseWorkspacePurpose =
  | 'support_case'
  | 'billing_review'
  | 'implementation'
  | 'security_incident'
  | 'data_import'
  | 'other';

export type SecureCaseWorkspaceStatus = 'open' | 'closed' | 'expired';

export type SecureCaseAccessRole = 'viewer' | 'contributor' | 'manager';
export type SecureCaseAccessStatus = 'pending' | 'active' | 'denied' | 'revoked' | 'expired';

export type SecureCaseDocumentStatus =
  | 'uploading'
  | 'scanning'
  | 'available'
  | 'rejected'
  | 'scan_failed'
  | 'deleting';

export type SecureCaseScanResult =
  | 'NO_THREATS_FOUND'
  | 'THREATS_FOUND'
  | 'UNSUPPORTED'
  | 'ACCESS_DENIED'
  | 'FAILED';

export type SecureCaseAuditEventType =
  | 'workspace_created'
  | 'workspace_viewed'
  | 'workspace_closed'
  | 'workspace_expired'
  | 'monday_link_updated'
  | 'access_requested'
  | 'access_granted'
  | 'access_denied'
  | 'access_revoked'
  | 'legal_hold_enabled'
  | 'legal_hold_disabled'
  | 'note_created'
  | 'note_updated'
  | 'note_deleted'
  | 'upload_started'
  | 'upload_completed'
  | 'scan_completed'
  | 'scan_rejected'
  | 'scan_failed'
  | 'document_previewed'
  | 'document_downloaded'
  | 'document_deleted'
  | 'access_denied_by_policy';

export type SecureCaseWorkspaceSummary = {
  id: string;
  clinicId: number;
  salesforceCaseId: string | null;
  salesforceCaseNumber: string | null;
  salesforceStatus: string | null;
  salesforceUrl: string | null;
  mondayItemUrl: string | null;
  purpose: SecureCaseWorkspacePurpose;
  status: SecureCaseWorkspaceStatus;
  retentionExpiresAt: number;
  legalHold: boolean;
  closedAt: number | null;
  createdAt: number;
  updatedAt: number;
  createdByAdminUserId: number;
  createdByAdminEmail: string;
  documentCount: number;
  noteCount: number;
  myAccessRole: SecureCaseAccessRole | null;
  myAccessStatus: SecureCaseAccessStatus | null;
  myAccessExpiresAt: number | null;
};

export type SecureCaseDocument = {
  id: string;
  workspaceId: string;
  clinicId: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  checksumSha256: string;
  status: SecureCaseDocumentStatus;
  scanResult: SecureCaseScanResult | null;
  uploadedByAdminUserId: number;
  uploadedByAdminEmail: string;
  createdAt: number;
  uploadCompletedAt: number | null;
  availableAt: number | null;
  expiresAt: number;
};

export type SecureCaseNote = {
  id: string;
  workspaceId: string;
  clinicId: number;
  body: string;
  createdByAdminUserId: number;
  createdByAdminEmail: string;
  createdAt: number;
  updatedAt: number;
};

export type SecureCaseWorkspaceAccess = {
  id: string;
  workspaceId: string;
  clinicId: number;
  adminUserId: number;
  adminEmail: string;
  adminName: string;
  role: SecureCaseAccessRole;
  status: SecureCaseAccessStatus;
  reason: string | null;
  requestedAt: number;
  grantedAt: number | null;
  grantedByAdminUserId: number | null;
  expiresAt: number;
  revokedAt: number | null;
};

export type SecureCaseAuditEvent = {
  id: number;
  workspaceId: string;
  clinicId: number;
  documentId: string | null;
  adminUserId: number | null;
  adminEmail: string | null;
  eventType: SecureCaseAuditEventType;
  outcome: 'allowed' | 'denied' | 'completed' | 'failed';
  metadata: Record<string, string | number | boolean | null> | null;
  createdAt: number;
};

export type SecureCaseWorkspaceDetailResponse = {
  workspace: SecureCaseWorkspaceSummary;
  documents: SecureCaseDocument[];
  notes: SecureCaseNote[];
  access: SecureCaseWorkspaceAccess[];
  auditEvents: SecureCaseAuditEvent[];
  salesforceSyncWarning?: boolean;
};

export type SecureCaseWorkspaceListResponse = {
  items: SecureCaseWorkspaceSummary[];
};

export type SecureCaseWorkspaceCreateRequest = {
  clinicId: number;
  salesforceCase?: string | null;
  purpose: SecureCaseWorkspacePurpose;
  mondayItemUrl?: string | null;
};

export type SecureCaseWorkspaceCreateResponse = {
  workspace: SecureCaseWorkspaceSummary;
};

export type SecureCaseSuccessResponse = {
  success: true;
};

export type SecureCaseWorkspaceUpdateRequest = {
  mondayItemUrl?: string | null;
  status?: Extract<SecureCaseWorkspaceStatus, 'open' | 'closed'>;
};

export type SecureCaseWorkspaceLegalHoldRequest = {
  enabled: boolean;
  reason: string;
};

export type SecureCaseUploadInitiateRequest = {
  fileName: string;
  contentType: string;
  sizeBytes: number;
  checksumSha256: string;
};

export type SecureCaseUploadGrant = {
  url: string;
  fields: Record<string, string>;
};

export type SecureCaseUploadInitiateResponse = {
  document: SecureCaseDocument;
  upload: SecureCaseUploadGrant;
};

export type SecureCaseDocumentResponse = {
  document: SecureCaseDocument;
};

export type SecureCaseDocumentUrlResponse = {
  url: string;
  expiresAt: number;
};

export type SecureCaseNoteCreateRequest = {
  body: string;
};

export type SecureCaseNoteUpdateRequest = {
  body: string;
};

export type SecureCaseNoteCreateResponse = {
  note: SecureCaseNote;
};

export type SecureCaseAccessRequestCreateRequest = {
  reason: string;
};

export type SecureCaseAccessGrantRequest = {
  adminEmail: string;
  role: SecureCaseAccessRole;
  expiresAt: number;
  reason: string;
};

export type SecureCaseAccessDecisionRequest = {
  status: 'active' | 'denied' | 'revoked';
  role?: SecureCaseAccessRole;
  expiresAt?: number;
  reason: string;
};
